
import { Building2, Shield } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { LenderRiskBadge } from "./LenderRiskBadge";
import { LenderMetrics } from "./LenderMetrics";
import { LenderLoanRange } from "./LenderLoanRange";
import { LenderActions } from "./LenderActions";

interface LenderCardProps {
  lender: {
    id: string;
    institution_name: string;
    total_loans_funded: number | null;
    risk_appetite: string | null;
    license_number: string | null;
    success_rate?: number;
    rating?: string;
    min_loan?: number;
    max_loan?: number;
    interest_rate?: string;
  };
  view?: "grid" | "list";
}

const LenderCard = ({ lender, view = "grid" }: LenderCardProps) => {
  return (
    <Card className={cn(
      "hover:shadow-lg transition-shadow border-l-4",
      lender.risk_appetite?.toLowerCase() === 'conservative' && "border-l-blue-500",
      lender.risk_appetite?.toLowerCase() === 'moderate' && "border-l-yellow-500",
      lender.risk_appetite?.toLowerCase() === 'aggressive' && "border-l-red-500",
      !lender.risk_appetite && "border-l-gray-500",
      view === "list" && "flex flex-col md:flex-row md:items-center"
    )}>
      <CardHeader className={cn(
        "md:flex-shrink-0",
        view === "list" && "md:w-1/4 md:border-r md:pr-6"
      )}>
        <CardTitle className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <span className="flex-1 text-xl">{lender.institution_name}</span>
          </div>
          {lender.license_number && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              <span>License: {lender.license_number}</span>
            </div>
          )}
          <LenderRiskBadge risk={lender.risk_appetite} />
        </CardTitle>
      </CardHeader>

      <CardContent className={cn(
        "space-y-6",
        view === "list" && "md:flex-1 md:space-y-4"
      )}>
        <LenderMetrics
          totalLoansFunded={lender.total_loans_funded}
          rating={lender.rating}
          successRate={lender.success_rate}
          interestRate={lender.interest_rate}
          view={view}
        />
        
        <LenderLoanRange
          minLoan={lender.min_loan}
          maxLoan={lender.max_loan}
        />

        <LenderActions
          lenderId={lender.id}
          view={view}
        />
      </CardContent>
    </Card>
  );
};

export default LenderCard;
